if (!customElements.get('track-order')) {
  customElements.define(
    'track-order',
    class TrackOrder extends HTMLElement {
      connectedCallback() {
        this.strings = VljStorefront.strings(this);
        this.form = this.querySelector('[data-track-form]');
        this.error = this.querySelector('[data-error]');
        this.result = this.querySelector('[data-result]');
        this.submitButton = this.querySelector('[data-submit]');

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.lookup();
        });
      }

      t(key, values) {
        return VljStorefront.format(this.strings[key], values);
      }

      async lookup() {
        const order = this.form.elements.order.value.trim();
        const email = this.form.elements.email.value.trim();
        if (!order || !this.form.elements.email.checkValidity() || !email) {
          this.showError(this.t('invalid'));
          return;
        }

        this.showError(null);
        this.setBusy(true);
        const { ok, data } = await VljStorefront.post('/track', { order, email });
        this.setBusy(false);

        if (!ok) {
          const key = { not_found: 'not_found', invalid: 'invalid', rate_limited: 'rate_limited' }[data.error] || 'unavailable';
          this.showError(this.t(key));
          return;
        }

        this.lastLookup = { order: data.order.name, email };
        this.render(data);
      }

      setBusy(busy) {
        this.submitButton.disabled = busy;
        this.submitButton.setAttribute('aria-busy', String(busy));
        this.submitButton.textContent = busy ? this.t('searching') : this.t('submit');
      }

      showError(message) {
        this.error.hidden = !message;
        this.error.textContent = message || '';
      }

      render(data) {
        const e = VljStorefront.escape;
        const { order, items, shipments, withdrawal } = data;

        this.result.innerHTML = `
          <div class="track-order__card">
            <div class="track-order__head">
              <div>
                <h2 class="h3 track-order__name">${e(order.name)}</h2>
                <p class="track-order__meta">${e(this.t('placed_on', { date: VljStorefront.formatDate(order.createdAt) }))}</p>
              </div>
              <span class="track-order__badge track-order__badge--${e(order.stage)}">${e(order.stageLabel)}</span>
            </div>
            ${order.stage === 'cancelled' ? `<p class="track-order__notice">${e(this.t('cancelled'))}</p>` : this.stepper(order.stage)}
            ${order.stage === 'problem' ? `<p class="track-order__notice track-order__notice--warn">${e(this.t('problem'))}</p>` : ''}
            ${order.stage === 'shipped' ? `<p class="track-order__notice">${e(this.t('no_updates'))}</p>` : ''}
            ${shipments.map((shipment) => this.shipment(shipment)).join('')}
            <h3 class="track-order__subheading">${e(this.t('items'))}</h3>
            <ul class="track-order__items" role="list">
              ${items
                .map(
                  (item) => `<li class="track-order__item">
                    ${item.image ? `<img src="${e(item.image)}" alt="" width="64" height="64" loading="lazy">` : '<span class="track-order__thumb" aria-hidden="true"></span>'}
                    <div>
                      <p class="track-order__item-title">${e(item.title)}</p>
                      <p class="track-order__meta">${[item.variantTitle, this.t('quantity', { quantity: item.quantity })]
                        .filter(Boolean)
                        .map(e)
                        .join(' · ')}</p>
                    </div>
                  </li>`
                )
                .join('')}
            </ul>
            ${this.withdrawalBlock(withdrawal)}
          </div>
          <button type="button" class="order-services__text-button track-order__again" data-again>${e(this.t('track_another'))}</button>
        `;

        this.form.hidden = true;
        this.result.hidden = false;
        this.result.focus();

        this.result.querySelector('[data-again]').addEventListener('click', () => {
          this.result.hidden = true;
          this.form.hidden = false;
          this.form.elements.order.value = '';
          this.form.elements.order.focus();
        });
        this.result.querySelector('[data-withdraw]')?.addEventListener('click', () => {
          VljStorefront.prefill.set(this.lastLookup);
          window.location.href = this.dataset.withdrawalUrl;
        });
      }

      stepper(stage) {
        const reached = { to_ship: 1, shipped: 2, in_transit: 3, problem: 3, delivered: 4 }[stage] || 1;
        const steps = ['step_ordered', 'step_shipped', 'step_in_transit', 'step_delivered'];
        return `<ol class="track-order__steps" role="list">
          ${steps
            .map((key, index) => {
              const state = index + 1 < reached ? 'done' : index + 1 === reached ? 'current' : 'todo';
              return `<li class="track-order__step track-order__step--${state}"${state === 'current' ? ' aria-current="step"' : ''}>
                <span class="track-order__dot" aria-hidden="true"></span>
                <span>${VljStorefront.escape(this.t(key))}</span>
              </li>`;
            })
            .join('')}
        </ol>`;
      }

      shipment(shipment) {
        const e = VljStorefront.escape;
        const date = shipment.deliveredAt
          ? this.t('delivered_on', { date: VljStorefront.formatDate(shipment.deliveredAt) })
          : shipment.estimatedDeliveryAt
          ? this.t('expected_on', { date: VljStorefront.formatDate(shipment.estimatedDeliveryAt) })
          : '';
        const rows = [
          shipment.carrier ? [this.t('carrier'), e(shipment.carrier)] : null,
          shipment.trackingNumber ? [this.t('tracking_number'), `<span class="track-order__mono">${e(shipment.trackingNumber)}</span>`] : null,
        ].filter(Boolean);
        if (!rows.length && !date) return '';

        return `<div class="track-order__shipment">
          <dl class="track-order__facts">
            ${rows.map(([term, value]) => `<div><dt>${e(term)}</dt><dd>${value}</dd></div>`).join('')}
          </dl>
          ${date ? `<p class="track-order__meta">${e(date)}</p>` : ''}
          ${
            shipment.trackingUrl
              ? `<a class="link track-order__track-link" href="${e(shipment.trackingUrl)}" target="_blank" rel="noopener">${e(
                  this.t('track_parcel')
                )} →</a>`
              : ''
          }
        </div>`;
      }

      withdrawalBlock(withdrawal) {
        const e = VljStorefront.escape;
        let text = '';
        let button = false;

        if (withdrawal.existing) {
          text = this.t('withdraw_existing', { reference: withdrawal.existing.reference });
        } else if (withdrawal.eligible) {
          text = withdrawal.deadline
            ? this.t('withdraw_until', { date: VljStorefront.formatDate(withdrawal.deadline) })
            : this.t('withdraw_open');
          button = true;
        } else if (withdrawal.reason === 'expired' && withdrawal.deadline) {
          text = this.t('withdraw_expired', { date: VljStorefront.formatDate(withdrawal.deadline) });
        } else if (withdrawal.reason === 'exempt') {
          text = this.t('withdraw_exempt');
        } else {
          return '';
        }

        return `<div class="track-order__withdrawal">
          <h3 class="track-order__subheading">${e(this.t('withdraw_heading'))}</h3>
          <p>${e(text)}</p>
          ${button ? `<button type="button" class="button button--secondary" data-withdraw>${e(this.t('withdraw_button'))}</button>` : ''}
        </div>`;
      }
    }
  );
}
